import {
	createFileRoute,
	Link,
	useNavigate,
	useRouteContext,
} from "@tanstack/react-router";
import {
	addEdge,
	Background,
	type Connection,
	Controls,
	type Edge,
	MarkerType,
	type Node,
	type NodeMouseHandler,
	ReactFlow,
	ReactFlowProvider,
	useEdgesState,
	useNodesState,
	useReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { createId } from "@paralleldrive/cuid2";
import { Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { toast } from "sonner";
import { Icon } from "@/components/icon";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
	type ActionData,
	AddButtonEdge,
	AddNodeDrawer,
	type InsertionContext,
	LookupContext,
	type LookupData,
	type NodeCategory,
	NodeConfigDrawer,
	nodeTypes,
	parseApiConnectionsToReactFlow,
	parseApiNodesToReactFlow,
	type TriggerData,
	transformParameters,
	WorkflowEditorContext,
	type WorkflowNodeData,
} from "@/components/workflows";
import type { workflowsWorkflowIdQuery } from "./__generated__/workflowsWorkflowIdQuery.graphql";

export const Route = createFileRoute(
	"/_logged/c/$club/(standalone)/workflows/$workflowId",
)({
	component: RouteComponent,
});

const getNodeId = () => createId();

const edgeTypes = { addButton: AddButtonEdge };

function WorkflowEditor() {
	const { club } = useRouteContext({ from: "/_logged/c/$club" });
	const { workflowId } = Route.useParams();
	const navigate = useNavigate();
	const { getViewport } = useReactFlow();
	const [workflowName, setWorkflowName] = useState("");
	const [isEditingName, setIsEditingName] = useState(false);
	const [isAddDrawerOpen, setIsAddDrawerOpen] = useState(false);
	const [selectedNode, setSelectedNode] =
		useState<Node<WorkflowNodeData> | null>(null);
	const [pendingNode, setPendingNode] = useState<WorkflowNodeData | null>(null);
	const [pendingParameterValues, setPendingParameterValues] = useState<
		Record<string, string | string[]>
	>({});
	const [isInitialized, setIsInitialized] = useState(false);
	const [insertionContext, setInsertionContext] =
		useState<InsertionContext | null>(null);

	const data = useLazyLoadQuery<workflowsWorkflowIdQuery>(
		graphql`
			query workflowsWorkflowIdQuery($club: String!, $workflowId: ID!) {
				automation(club: $club) {
					triggers {
						key
						name
						description
						icon
						type
						canRunOnCreate
						runOnCreateDescription
						conditions {
							key
							name
							type
							required
							options
						}
					}
					actions {
						type
						name
						description
						icon
						parameters {
							key
							name
							type
							required
							options
						}
					}
				}
				club(slug: $club) {
					tags(first: 1000) {
						edges {
							node {
								id
								rawID
								text
							}
						}
					}
					badges(first: 1000) {
						edges {
							node {
								id
								rawID
								text
							}
						}
					}
					templates(first: 1000) {
						edges {
							node {
								id
								rawID
								title
							}
						}
					}
				}
				workflow: node(id: $workflowId) {
					... on Workflow {
						id
						name
						active
						nodes {
							id
							type
							enabled
							trigger
							parameters
							position {
								x
								y
							}
						}
						connections
						createdAt
						updatedAt
					}
				}
			}
		`,
		{ club: club.slug, workflowId },
	);

	const triggers = data.automation.triggers as unknown as TriggerData[];
	const actions = data.automation.actions as unknown as ActionData[];

	const [nodes, setNodes, onNodesChange] = useNodesState<
		Node<WorkflowNodeData>
	>([]);
	const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);

	// Initialize nodes and edges from workflow data
	useEffect(() => {
		if (data.workflow && !isInitialized) {
			const workflow = data.workflow as {
				id: string;
				name: string;
				active: boolean;
				nodes: Array<{
					id: string;
					type: string;
					enabled: boolean;
					trigger: boolean;
					parameters: unknown;
					position: { x: number; y: number } | null;
				}>;
				connections: unknown;
			};

			setWorkflowName(workflow.name);

			// Nodes come as array from GraphQL, connections as JSON
			const apiNodes = workflow.nodes.map((node) => ({
				...node,
				parameters: node.parameters as Record<string, unknown>,
				position: node.position ?? { x: 0, y: 0 },
			}));
			const apiConnections = workflow.connections as Array<{
				id: string;
				type: "default" | "ifTrue" | "ifFalse";
				index: number;
				source: string;
				target: string;
			}>;

			// Convert to ReactFlow format
			const reactFlowNodes = parseApiNodesToReactFlow(
				apiNodes,
				triggers,
				actions,
			);
			const reactFlowEdges = parseApiConnectionsToReactFlow(apiConnections);

			setNodes(reactFlowNodes);
			setEdges(reactFlowEdges);
			setIsInitialized(true);
		}
	}, [data.workflow, triggers, actions, isInitialized, setNodes, setEdges]);

	const [commitUpdateWorkflow, isUpdating] = useMutation(graphql`
		mutation workflowsWorkflowIdUpdateMutation($payload: UpdateWorkflowInput!) {
			updateWorkflow(payload: $payload) {
				id
				name
				createdAt
				updatedAt
				active
			}
		}
	`);

	const onConnect = useCallback(
		(params: Connection) => {
			// Add index property based on sourceHandle (for condition nodes)
			// sourceHandle "0" = true path (index 0), "1" = false path (index 1)
			const index = params.sourceHandle
				? Number.parseInt(params.sourceHandle, 10)
				: 0;

			// Style edges based on condition path (green for true, red for false)
			const isConditionEdge =
				params.sourceHandle === "0" || params.sourceHandle === "1";
			const edgeColor = isConditionEdge
				? index === 0
					? "#10b981" // emerald-500 for true
					: "#ef4444" // red-500 for false
				: "#34d399"; // default emerald-400

			const edgeWithIndex = {
				...params,
				type: "addButton",
				style: { stroke: edgeColor, strokeWidth: 2 },
				markerEnd: { type: MarkerType.ArrowClosed, color: edgeColor },
				data: { index },
			};
			setEdges((eds) => addEdge(edgeWithIndex, eds));
		},
		[setEdges],
	);

	const handleSave = useCallback(() => {
		// Find the trigger node
		const triggerNode = nodes.find((n) => n.data.category === "trigger");
		if (!triggerNode) {
			console.error("No trigger node found");
			return;
		}

		// Transform edges to connections format
		const connections = edges.map((edge) => {
			const sourceNode = nodes.find((n) => n.id === edge.source);
			const isConditionSource = sourceNode?.data.nodeType === "IF";
			const index = edge.data?.index ?? 0;

			let type = "default";
			if (isConditionSource) {
				type = index === 0 ? "ifTrue" : "ifFalse";
			}

			return {
				source: edge.source,
				target: edge.target,
				id: edge.id,
				type,
				index,
			};
		});

		// Transform nodes to API format
		const apiNodes = nodes.map((node) => ({
			id: node.id,
			type: node.data.nodeType,
			trigger: node.data.category === "trigger",
			enabled: true,
			parameters: transformParameters(
				node.data.nodeType,
				node.data.parameterValues,
			),
			position: node.position,
		}));

		const payload = {
			id: workflowId,
			name: workflowName,
			active: true,
			triggerType: triggerNode.data.nodeType,
			triggerParameters: transformParameters(
				triggerNode.data.nodeType,
				triggerNode.data.parameterValues,
			),
			connections,
			nodes: apiNodes,
		};

		commitUpdateWorkflow({
			variables: {
				payload,
			},
			onCompleted: (_response, errors) => {
				if (errors?.length) {
					toast.error(errors[0].message);
					return;
				}
				toast.success("Workflow atualizado com sucesso!");
				navigate({
					to: "/c/$club/automacoes/workflows",
					params: { club: club.slug },
				});
			},
			onError: (error) => {
				console.error("Error updating workflow:", error);
				toast.error("Erro ao atualizar workflow.");
			},
		});
	}, [
		nodes,
		edges,
		workflowName,
		club.slug,
		workflowId,
		commitUpdateWorkflow,
		navigate,
	]);

	const startAddingNode = useCallback(
		(nodeData: Omit<WorkflowNodeData, "parameterValues">) => {
			setPendingNode({ ...nodeData, parameterValues: {} });
			setPendingParameterValues({});
			setIsAddDrawerOpen(false);
		},
		[],
	);

	const confirmAddNode = useCallback(() => {
		if (!pendingNode) return;

		const getNodeType = (category: NodeCategory) => {
			switch (category) {
				case "trigger":
					return "trigger";
				case "condition":
					return "condition";
				case "action":
					return "action";
				case "wait":
					return "wait";
			}
		};

		const newNodeId = getNodeId();

		if (insertionContext?.type === "edge") {
			// Insert node in the middle of an edge
			const edge = edges.find((e) => e.id === insertionContext.edgeId);
			if (!edge) return;

			const sourceNode = nodes.find((n) => n.id === insertionContext.source);
			const targetNode = nodes.find((n) => n.id === insertionContext.target);

			if (!sourceNode || !targetNode) return;

			// Position at midpoint between source and target
			const midX = (sourceNode.position.x + targetNode.position.x) / 2;
			const midY = (sourceNode.position.y + targetNode.position.y) / 2;

			const newNode: Node<WorkflowNodeData> = {
				id: newNodeId,
				type: getNodeType(pendingNode.category),
				position: { x: midX, y: midY },
				data: {
					...pendingNode,
					parameterValues: pendingParameterValues,
				},
			};

			// Remove old edge, add node, and create two new edges
			setEdges((eds) => {
				const filtered = eds.filter((e) => e.id !== insertionContext.edgeId);

				const sourceEdgeColor = edge.style?.stroke ?? "#34d399";
				const newEdge1: Edge = {
					id: `e-${insertionContext.source}-${newNodeId}`,
					source: insertionContext.source,
					target: newNodeId,
					sourceHandle: insertionContext.sourceHandle,
					type: "addButton",
					style: { stroke: sourceEdgeColor as string, strokeWidth: 2 },
					markerEnd: {
						type: MarkerType.ArrowClosed,
						color: sourceEdgeColor as string,
					},
					data: { index: edge.data?.index ?? 0 },
				};

				const newEdge2: Edge = {
					id: `e-${newNodeId}-${insertionContext.target}`,
					source: newNodeId,
					target: insertionContext.target,
					type: "addButton",
					style: { stroke: "#34d399", strokeWidth: 2 },
					markerEnd: {
						type: MarkerType.ArrowClosed,
						color: "#34d399",
					},
					data: { index: 0 },
				};

				return [...filtered, newEdge1, newEdge2];
			});

			setNodes((nds) => [...nds, newNode]);
		} else if (insertionContext?.type === "leaf") {
			// Insert node below a leaf node
			const parentNode = nodes.find((n) => n.id === insertionContext.nodeId);
			if (!parentNode) return;

			const newNode: Node<WorkflowNodeData> = {
				id: newNodeId,
				type: getNodeType(pendingNode.category),
				position: {
					x: parentNode.position.x,
					y: parentNode.position.y + 150,
				},
				data: {
					...pendingNode,
					parameterValues: pendingParameterValues,
				},
			};

			// Determine edge color based on handle
			const handleId = insertionContext.handleId;
			const isConditionHandle = handleId === "0" || handleId === "1";
			const index = handleId ? Number.parseInt(handleId, 10) : 0;
			const edgeColor = isConditionHandle
				? index === 0
					? "#10b981"
					: "#ef4444"
				: "#34d399";

			const newEdge: Edge = {
				id: `e-${insertionContext.nodeId}-${newNodeId}`,
				source: insertionContext.nodeId,
				target: newNodeId,
				sourceHandle: handleId,
				type: "addButton",
				style: { stroke: edgeColor, strokeWidth: 2 },
				markerEnd: {
					type: MarkerType.ArrowClosed,
					color: edgeColor,
				},
				data: { index },
			};

			setNodes((nds) => [...nds, newNode]);
			setEdges((eds) => [...eds, newEdge]);
		} else {
			// Default: place at viewport center
			const viewport = getViewport();
			const centerX =
				(-viewport.x + window.innerWidth / 2) / viewport.zoom - 75;
			const centerY =
				(-viewport.y + window.innerHeight / 2) / viewport.zoom - 25;

			const newNode: Node<WorkflowNodeData> = {
				id: newNodeId,
				type: getNodeType(pendingNode.category),
				position: { x: centerX, y: centerY },
				data: {
					...pendingNode,
					parameterValues: pendingParameterValues,
				},
			};

			setNodes((nds) => [...nds, newNode]);
		}

		setPendingNode(null);
		setPendingParameterValues({});
		setInsertionContext(null);
	}, [
		pendingNode,
		pendingParameterValues,
		insertionContext,
		getViewport,
		setNodes,
		setEdges,
		nodes,
		edges,
	]);

	const cancelAddNode = useCallback(() => {
		setPendingNode(null);
		setPendingParameterValues({});
		setInsertionContext(null);
		setIsAddDrawerOpen(true);
	}, []);

	const onNodeClick: NodeMouseHandler<Node<WorkflowNodeData>> = useCallback(
		(_, node) => {
			setSelectedNode(node);
		},
		[],
	);

	const deleteNode = useCallback(
		(nodeId: string) => {
			setNodes((nds) => nds.filter((node) => node.id !== nodeId));
			setEdges((eds) =>
				eds.filter((edge) => edge.source !== nodeId && edge.target !== nodeId),
			);
			setSelectedNode(null);
		},
		[setNodes, setEdges],
	);

	const updateNodeParameter = useCallback(
		(nodeId: string, paramKey: string, value: string | string[]) => {
			setNodes((nds) =>
				nds.map((node) => {
					if (node.id === nodeId) {
						return {
							...node,
							data: {
								...node.data,
								parameterValues: {
									...node.data.parameterValues,
									[paramKey]: value,
								},
							},
						};
					}
					return node;
				}),
			);
			setSelectedNode((prev) => {
				if (prev && prev.id === nodeId) {
					return {
						...prev,
						data: {
							...prev.data,
							parameterValues: {
								...prev.data.parameterValues,
								[paramKey]: value,
							},
						},
					};
				}
				return prev;
			});
		},
		[setNodes],
	);

	const updatePendingParameter = useCallback(
		(paramKey: string, value: string | string[]) => {
			setPendingParameterValues((prev) => ({
				...prev,
				[paramKey]: value,
			}));
		},
		[],
	);

	const onInsertOnEdge = useCallback(
		(edgeId: string, source: string, target: string, sourceHandle?: string) => {
			setInsertionContext({
				type: "edge",
				edgeId,
				source,
				target,
				sourceHandle,
			});
			setIsAddDrawerOpen(true);
		},
		[],
	);

	const onInsertAtLeaf = useCallback((nodeId: string, handleId?: string) => {
		setInsertionContext({ type: "leaf", nodeId, handleId });
		setIsAddDrawerOpen(true);
	}, []);

	const editorContextValue = useMemo(
		() => ({ onInsertOnEdge, onInsertAtLeaf }),
		[onInsertOnEdge, onInsertAtLeaf],
	);

	// Prepare lookup data for node descriptions
	const lookupData: LookupData = {
		tags: data.club?.tags?.edges?.map((e) => e.node) ?? [],
		badges: data.club?.badges?.edges?.map((e) => e.node) ?? [],
		templates: data.club?.templates?.edges?.map((e) => e.node) ?? [],
	};

	// Determine which node data to show in the config drawer
	const configNode = pendingNode || selectedNode?.data;
	const configParameterValues = pendingNode
		? pendingParameterValues
		: selectedNode?.data.parameterValues || {};
	const isNewNode = pendingNode !== null;

	return (
		<LookupContext.Provider value={lookupData}>
			<WorkflowEditorContext.Provider value={editorContextValue}>
				<div className="flex flex-col h-screen bg-background">
					{/* Header */}
					<header className="sticky top-0 z-10 flex items-center justify-between h-14 px-4 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
						<div className="flex items-center gap-2 text-sm">
							<Link
								to="/c/$club/automacoes/workflows"
								params={{ club: club.slug }}
								className="text-muted-foreground hover:text-foreground transition-colors"
							>
								Workflows
							</Link>
							<span className="text-muted-foreground">/</span>
							{isEditingName ? (
								<Input
									autoFocus
									className="h-7 w-48 text-sm font-medium"
									value={workflowName}
									onChange={(e) => setWorkflowName(e.target.value)}
									onBlur={() => setIsEditingName(false)}
									onKeyDown={(e) => {
										if (e.key === "Enter" || e.key === "Escape") {
											setIsEditingName(false);
										}
									}}
								/>
							) : (
								<button
									type="button"
									onClick={() => setIsEditingName(true)}
									className="flex items-center gap-1.5 font-medium hover:text-primary transition-colors group"
								>
									{workflowName}
									<Icon
										name="Pencil"
										className="size-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity"
									/>
								</button>
							)}
						</div>

						<div className="flex items-center gap-2">
							<Button type="button" variant="ghost" size="sm" asChild>
								<Link
									to="/c/$club/automacoes/workflows"
									params={{ club: club.slug }}
								>
									Cancelar
								</Link>
							</Button>
							<Button size="sm" onClick={handleSave} disabled={isUpdating}>
								{isUpdating ? (
									<Icon name="Loader" className="size-4 animate-spin" />
								) : (
									<Icon name="Check" className="size-4" />
								)}
								{isUpdating ? "Salvando..." : "Salvar"}
							</Button>
						</div>
					</header>

					{/* Main Content */}
					<div className="flex-1 relative">
						<ReactFlow
							nodes={nodes}
							edges={edges}
							nodeTypes={nodeTypes}
							edgeTypes={edgeTypes}
							defaultEdgeOptions={{
								type: "addButton",
								style: { stroke: "#34d399", strokeWidth: 2 },
								markerEnd: { type: MarkerType.ArrowClosed, color: "#34d399" },
							}}
							onNodesChange={onNodesChange}
							onEdgesChange={onEdgesChange}
							onConnect={onConnect}
							onNodeClick={onNodeClick}
							fitView
							proOptions={{ hideAttribution: true }}
						>
							<Background />
							<Controls />
						</ReactFlow>
					</div>

					{/* Add Node Drawer */}
					<AddNodeDrawer
						open={isAddDrawerOpen}
						onOpenChange={setIsAddDrawerOpen}
						triggers={triggers}
						actions={actions}
						onAddNode={startAddingNode}
						hideTriggers={nodes.some((n) => n.data.category === "trigger")}
					/>

					{/* Node Config Drawer */}
					<NodeConfigDrawer
						configNode={configNode ?? null}
						parameterValues={configParameterValues}
						isNewNode={isNewNode}
						lookupData={lookupData}
						runOnCreate={false}
						onRunOnCreateChange={() => {}}
						onParameterChange={(key, value) => {
							if (isNewNode) {
								updatePendingParameter(key, value);
							} else if (selectedNode) {
								updateNodeParameter(selectedNode.id, key, value);
							}
						}}
						onConfirm={confirmAddNode}
						onCancel={cancelAddNode}
						onDelete={
							selectedNode ? () => deleteNode(selectedNode.id) : undefined
						}
						onClose={() => setSelectedNode(null)}
						hideTriggerBehavior
					/>
				</div>
			</WorkflowEditorContext.Provider>
		</LookupContext.Provider>
	);
}

function WorkflowSkeleton() {
	return (
		<div className="flex flex-col h-screen bg-background">
			<header className="flex items-center justify-between h-14 px-4 border-b">
				<div className="flex items-center gap-2">
					<Skeleton className="h-4 w-20" />
					<span className="text-muted-foreground">/</span>
					<Skeleton className="h-4 w-32" />
					<Skeleton className="h-5 w-16 ml-2" />
				</div>
				<div className="flex items-center gap-2">
					<Skeleton className="h-8 w-20" />
					<Skeleton className="h-8 w-20" />
				</div>
			</header>
			<div className="flex-1 flex items-center justify-center">
				<Skeleton className="size-16 rounded-full" />
			</div>
		</div>
	);
}

function RouteComponent() {
	return (
		<ReactFlowProvider>
			<Suspense fallback={<WorkflowSkeleton />}>
				<WorkflowEditor />
			</Suspense>
		</ReactFlowProvider>
	);
}
